/**
 * Noibu Feature Flag: always-visible mobile search bar
 * (flag "2083-always-visible-mobile-search-bar", Noibu A/B test 116).
 *
 * Structurally parallel to assets/noibu-flag-cart.js. It is a separate file on
 * purpose: the other flag files define controls for running experiments and
 * must not change mid-flight.
 *
 * The variation is server-rendered by sections/noibu-search-ab.liquid. This
 * script builds no DOM; it sets a class on <html> that decides whether the
 * already-rendered search bar is visible. A flag failure is therefore a no-op
 * and the header renders exactly as it does today.
 *
 *   original                   -> html.nff-search--a  (control, the default)
 *   open-search-bar-in-header  -> html.nff-search--b
 *
 * The flag is read exactly as Noibu's generated implementation code does:
 * client.getStringDetails(FLAG, 'original') and a switch on `variation`, whose
 * values are the variation keys from the Noibu test (see PR #140 for why the
 * "2083-" domain prefix on the flag key matters).
 *
 * Targeting (device type is mobile) lives in the Noibu test. Visitors outside
 * it get the default ('original') and land on the control.
 */
(function () {
  'use strict';

  var FLAG = '2083-always-visible-mobile-search-bar';
  var CONTROL = 'original';
  var VARIATION_B = 'open-search-bar-in-header';
  var CACHE_KEY = 'nff.mobile-search-bar';

  var REPORT_KEY = 'nff_mobile-search-bar';
  var STATE_ATTR = 'data-nff-search-state';

  // Anchored to window.load because the client cannot arrive before it:
  // collect-core is injected by a ScriptTag on load, and collect-ff by
  // collect-core after that.
  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-search--a';
  CLASSES[VARIATION_B] = 'nff-search--b';

  var root = document.documentElement;

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    // NOIBUJS.addCustomAttribute comes from collect-core, which loads
    // independently of collect-ff, so reporting works even when flags do not.
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // collect-core may still be booting. Retry rather than drop the exposure.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load. Is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function syncHeaderHeight() {
    // Dawn's StickyHeader caches --header-height when it connects. The bar
    // lives in the header group, so refresh it once the arm is applied or
    // the predictive search and menu drawer open at the wrong offset.
    var header = document.querySelector('.section-header');
    if (header) {
      root.style.setProperty('--header-height', header.offsetHeight + 'px');
    }
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (root.classList.contains(className)) return;

    root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
    root.classList.add(className);
    root.setAttribute('data-nff-search', variation);
    syncHeaderHeight();

    document.dispatchEvent(
      new CustomEvent('noibu:search-variation', { detail: { variation: variation } })
    );
  }

  function remember(variation) {
    // Cached so a returning collector (this store's main customer) does not
    // watch the bar appear after load. The SDK stays the source of truth;
    // this only feeds the paint-time hint in the gate section.
    try {
      window.localStorage.setItem(CACHE_KEY, variation);
    } catch (error) {
      /* no-op: private mode or an embedded webview */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_search=open-search-bar-in-header
    try {
      var value = new URLSearchParams(window.location.search).get('nff_search');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable: fall through to the flag. */
    }
    return null;
  }

  function readVariation(client) {
    // Noibu's implementation code for test 116 (console > A/B tests >
    // Implementation):
    //   const { variation } = client.getStringDetails(FLAG, 'original');
    //   switch (variation) { case 'open-search-bar-in-header': ... }
    // Verified on this store on 2026-10-01: the details object the SDK
    // returns carries the arm as `variant` (and `value`), not `variation`,
    // e.g. { variant: 'show-reassurance', value: 'show-reassurance',
    // reason: 'CACHED' } for 2083-cart-reassurance. Destructuring
    // `variation` alone would always fall through to the control, so read
    // all three.
    if (typeof client.getStringDetails === 'function') {
      var details = client.getStringDetails(FLAG, CONTROL) || {};
      return details.variant || details.variation || details.value || CONTROL;
    }
    // Older SDK builds without getStringDetails.
    return client.getStringValue(FLAG, CONTROL);
  }

  function render(client) {
    var variation = readVariation(client);

    if (variation === VARIATION_B) {
      activate(VARIATION_B);
      remember(VARIATION_B);
      return VARIATION_B;
    }

    // Control, visitors excluded by targeting, and unresolved flags.
    activate(CONTROL);
    remember(CONTROL);
    return CONTROL;
  }

  var forced = forcedVariation();
  if (forced) {
    // Skips remember() and the SDK so a forced preview neither poisons the
    // visitor's cache nor records an exposure.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Do NOT remember() here: caching a value the SDK never produced would
      // replay a decision that was never made. A returning visitor keeps the
      // arm replayed from their earlier, real assignment (same as the cart
      // and PDP flags) so the bar does not vanish mid-visit.
      if (!root.hasAttribute('data-nff-search')) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
